import { useEffect, useState } from "react";
import { api } from "@/lib/auth";
import { Loader2, TrendingUp, AlertTriangle } from "lucide-react";

const aud = (cents) => `$${((cents || 0) / 100).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const ProfitabilityReport = () => {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/reports/profitability").then(({ data }) => setData(data)).catch(() => setData({ rows: [], totals: {} }));
    }, []);
    if (!data) return <Loader2 size={16} className="animate-spin text-[#FF5A00]" />;
    if (data.rows.length === 0) return null;
    const { totals } = data;
    return (
        <section className="mt-10" data-testid="profitability-report">
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <TrendingUp size={14} className="text-[#FF5A00]" />
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-slate-400">Campaign profitability · cost vs sell</span>
                <span className="ml-auto text-[10px] font-mono-tech text-slate-500">
                    Total margin <span className={totals.margin_cents >= 0 ? "text-emerald-300" : "text-red-300"} data-testid="profitability-total-margin">{aud(totals.margin_cents)}</span>
                    {totals.margin_pct !== null && totals.margin_pct !== undefined && ` (${totals.margin_pct}%)`}
                </span>
            </div>
            <div className="overflow-x-auto border border-white/10">
                <table className="w-full text-xs" data-testid="profitability-table">
                    <thead><tr className="border-b border-white/10 text-left">
                        {["Campaign", "Screens", "Days", "Revenue", "Screen cost", "Margin", "%"].map((h) => (
                            <th key={h} className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-slate-500">{h}</th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {data.rows.map((r) => (
                            <tr key={r.campaign_id} className="border-b border-white/5" data-testid={`profitability-row-${r.campaign_id}`}>
                                <td className="px-3 py-2 text-white">
                                    {r.business_name}
                                    {r.missing_cost_screens > 0 && (
                                        <span title={`${r.missing_cost_screens} screen(s) missing a cost price — margin understated`}>
                                            <AlertTriangle size={10} className="inline ml-1.5 text-amber-400" />
                                        </span>
                                    )}
                                    <div className="text-[9px] font-mono-tech text-slate-500">{r.launch_date} · {r.pipeline_status}</div>
                                </td>
                                <td className="px-3 py-2 text-slate-400">{r.screens}</td>
                                <td className="px-3 py-2 text-slate-400">{r.booked_days}</td>
                                <td className="px-3 py-2 text-white">{aud(r.revenue_cents)} <span className="text-[8px] font-mono-tech text-slate-600 uppercase">{r.revenue_source}</span></td>
                                <td className="px-3 py-2 text-slate-400">{aud(r.cost_cents)}</td>
                                <td className={`px-3 py-2 font-semibold ${r.margin_cents >= 0 ? "text-emerald-300" : "text-red-300"}`}>{aud(r.margin_cents)}</td>
                                <td className="px-3 py-2 text-slate-400">{r.margin_pct === null ? "—" : `${r.margin_pct}%`}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export default ProfitabilityReport;
